// frontend/src/components/OAuthCallbackHandler.jsx

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from '../config/routes';
import { Box, CircularProgress, Typography, Alert, Container, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { handleOAuthCallback } from '../api/oauthApi.js';

const CallbackContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  padding: theme.spacing(2),
}));

const CallbackPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  maxWidth: 400,
  width: '100%',
}));

const LoadingBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

/**
 * OAuth Callback Handler Component
 * Handles the OAuth callback flow and completes authentication
 */
function OAuthCallbackHandler({
  onSuccess,
  onError,
  successRedirect = '/dashboard',
  errorRedirect = routes.login(),
}) {
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Completing authentication...');
  const [error, setError] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      // Parse URL parameters
      const urlParams = new URLSearchParams(location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');

      // Check for OAuth errors
      if (error) {
        throw new Error(`OAuth error: ${error} - ${errorDescription || 'Authentication failed'}`);
      }

      // Validate required parameters
      if (!code || !state) {
        throw new Error('Missing required OAuth parameters');
      }

      // Retrieve stored state and provider
      const storedState = sessionStorage.getItem('oauth_state');
      const storedProvider = sessionStorage.getItem('oauth_provider');

      if (!storedState || !storedProvider) {
        throw new Error('OAuth state not found. Please restart the login process.');
      }

      // Validate state parameter for CSRF protection
      if (state !== storedState) {
        throw new Error('Invalid state parameter. Possible CSRF attack detected.');
      }

      setMessage('Exchanging authorization code...');

      // Handle OAuth callback
      const result = await handleOAuthCallback(storedProvider, code, state);

      // Clean up stored state
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('oauth_provider');

      setStatus('success');
      setMessage('Authentication successful! Redirecting...');

      // Store authentication data
      if (result.token) {
        localStorage.setItem('token', result.token);
      }

      if (result.user) {
        localStorage.setItem('user', JSON.stringify(result.user));
      }

      // Call success callback
      if (onSuccess) {
        onSuccess(result);
      }

      // Redirect after a short delay
      setTimeout(() => {
        const redirectTo = sessionStorage.getItem('oauth_redirect') || successRedirect;
        sessionStorage.removeItem('oauth_redirect');
        navigate(redirectTo, { replace: true });
      }, 1500);
    } catch (error) {
      console.error('OAuth callback error:', error);

      setStatus('error');
      setError(error.message);
      setMessage('Authentication failed');

      // Clean up stored state
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('oauth_provider');

      // Call error callback
      if (onError) {
        onError(error);
      }

      // Redirect to error page after a delay
      setTimeout(() => {
        navigate(errorRedirect, { replace: true });
      }, 3000);
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'primary';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return <CircularProgress size={48} color="primary" />;
      case 'success':
        return <Box sx={{ color: 'success.main', fontSize: 48 }}>✓</Box>;
      case 'error':
        return <Box sx={{ color: 'error.main', fontSize: 48 }}>✗</Box>;
      default:
        return <CircularProgress size={48} color="primary" />;
    }
  };

  return (
    <CallbackContainer>
      <CallbackPaper elevation={3}>
        <LoadingBox>
          {getStatusIcon()}

          <Typography variant="h6" color={getStatusColor()}>
            {message}
          </Typography>

          {status === 'processing' && (
            <Typography variant="body2" color="text.secondary">
              Please wait while we complete your authentication...
            </Typography>
          )}

          {status === 'success' && (
            <Typography variant="body2" color="text.secondary">
              You will be redirected shortly.
            </Typography>
          )}

          {status === 'error' && error && (
            <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
              {error}
            </Alert>
          )}

          {status === 'error' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              You will be redirected to the login page.
            </Typography>
          )}
        </LoadingBox>
      </CallbackPaper>
    </CallbackContainer>
  );
}

export default OAuthCallbackHandler;
